import type { OnFinishReturnObject, SimpleFormRef } from '@evidia/components';
import { SimpleForm } from '@evidia/components';
import { DrivingLessonRecordType } from '@evidia/shared';
import { useRef } from 'react';
import styled from 'styled-components';
import { useActiveSchoolId } from '../../../../hooks/useActiveSchoolId';
import { mergeDateAndTime } from '../../../../utils/date';
import { trpc } from '../../../../utils/trcp';
import type { DefaultTeacherType } from '../../../DefaultTeacher';
import MainTitle from '../../../MainTitle';
import type { DrivingLessonData } from '../../hooks/useDrivingLessonsData';
import useTrainingFields from './useTrainingFileds';

const Title = styled(MainTitle)`
	text-align: left;
`;

const getLastDrivingLessonTachometer = (drivingLessons: DrivingLessonData) => {
	const lastDrivingLesson = drivingLessons[DrivingLessonRecordType.DRIVING]?.find(lesson => lesson.lessonHours);

	return lastDrivingLesson?.tachometerAfter ?? null;
};

interface Props {
	type: 'plan' | 'create';
	studentId: string;
	defaultTeacher?: DefaultTeacherType;
	drivingLessonData: DrivingLessonData;
	onClose: () => void;
}

const TrainingPlan: React.FC<Props> = ({ onClose, drivingLessonData, type, defaultTeacher, studentId }) => {
	const { activeSchoolId } = useActiveSchoolId();
	const createPlanDrivingLessonsRecordPlanV2 =
		trpc.drivingLessonsRecord.createPlanDrivingLessonsRecordPlanV2.useMutation();
	const simpleFormRef = useRef<SimpleFormRef | null>(null);

	const onFormFinish = async (values: {
		startTimeDate: Date;
		startTimeTime: Date;
		length: string;
		endTimeDate: Date;
		endTimeTime: Date;
		teacher: string;
		car: string;

		signature?: string;
		teacherSignature?: string;
		tachometerAfter?: string;
		tachometerBefore?: string;
		type: DrivingLessonRecordType;
	}): Promise<OnFinishReturnObject> => {
		const startTime = mergeDateAndTime(values.startTimeDate, values.startTimeTime);
		const endTime = mergeDateAndTime(values.endTimeDate, values.endTimeTime);

		if (type === 'create') {
			const tachometerBefore = values.tachometerBefore ? parseInt(values.tachometerBefore, 10) : undefined;
			const tachometerAfter = values.tachometerAfter ? parseInt(values.tachometerAfter, 10) : undefined;
			const length = values.length ? parseInt(values.length, 10) : undefined;

			const drivingPayload = {
				tachometerBefore,
				tachometerAfter,
				length,
			};

			await createPlanDrivingLessonsRecordPlanV2.mutateAsync({
				studentId,
				userId: values.teacher === '__empty' ? undefined : values.teacher,
				schoolCarId: values.car === '__empty' ? undefined : values.car,
				startTime,
				endTime,
				schoolId: activeSchoolId,

				signature: values.signature,
				teacherSignature: values.teacherSignature,

				type: values.type as any,
				...(values.type === DrivingLessonRecordType.DRIVING ? drivingPayload : {}),
			});

			return {
				status: 'success',
				title: 'Hotovo',
				subtitle: 'Výcvik byla zaevidován',
				actionButtonFn: () => {
					onClose();
					setTimeout(() => {
						simpleFormRef.current?.resetForm();
					}, 1000);
				},
			};
		}

		const drivingPayload = {
			length: parseInt(values.length, 10),
		};

		await createPlanDrivingLessonsRecordPlanV2.mutateAsync({
			studentId,
			userId: values.teacher === '__empty' ? undefined : values.teacher,
			schoolCarId: values.car === '__empty' ? undefined : values.car,
			startTime,
			endTime,
			schoolId: activeSchoolId,
			type: values.type as any,

			...(values.type === DrivingLessonRecordType.DRIVING ? drivingPayload : {}),
		});

		return {
			status: 'success',
			title: 'Hotovo',
			subtitle: 'Výcvik byl naplánován',
			actionButtonFn: () => {
				onClose();
				setTimeout(() => {
					simpleFormRef.current?.resetForm();
				}, 1000);
			},
		};
	};

	const {
		fields: formFields,
		trainingFieldsMap,
		triggers,
	} = useTrainingFields({
		type,
		referer: 'studen-card',
	});

	return (
		<>
			<Title>{type === 'create' ? 'Zaevidovat výcvik' : 'Naplánovat výcvik'}</Title>
			<SimpleForm
				ref={simpleFormRef}
				initialValues={{
					type: DrivingLessonRecordType.DRIVING,
					length: '90',
					teacher: defaultTeacher?.id,
					tachometerBefore: getLastDrivingLessonTachometer(drivingLessonData),
				}} // todo default teacher and car
				fields={formFields}
				onFinish={onFormFinish}
				fieldsGridMap={trainingFieldsMap}
				lessSpaceBetweenItems
				submitButtonTitle={type === 'create' ? 'Zaevidovat výcvik' : 'Naplánovat výcvik'}
				triggers={triggers}
			/>
		</>
	);
};

export default TrainingPlan;
